import type {ReactElement} from 'react';
import {useState} from 'react';
import {Panel, PanelContent, PanelHeader} from '@Components/panel';
import {Button, Size, Type} from '@Components/button';
import {Text, TextSize} from '@Components/text';
import {Checkbox} from '@Components/checkbox';
import {IconSize} from '@Components/icon-v2/icon.types';
import {setCookie} from '@Utils/cookie.util';
import {useAppDispatch} from '@/hooks';
import {qrCodeFlow} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {getEditPosterURL} from '@Libraries/poster-editor.library';
import {useWebLinks} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';

const REDIRECT_CONFIRMATION_QR_TEMPLATE_COOKIE_NAME = 'pmwrcqrt';

export interface PublishingRedirectConfirmationPanelProps {
  onClose: () => void;
}

export function PublishingRedirectConfirmationPanel({...props}: PublishingRedirectConfirmationPanelProps): ReactElement {
  const [isChecked, setIsChecked] = useState(false);
  const webLink = useWebLinks();
  const dispatch = useAppDispatch();

  const getHeader = (): ReactElement<any> => {
    return (
      <PanelHeader
        title={window.i18next.t('pmwjs_confirmation')}
        showBackButton
        onBackButton={(): void => {
          dispatch(qrCodeFlow());
        }}
        showCloseButton
        onClose={(): void => {
          props.onClose();
        }}
        centerAlignTitle
        isOpenedInModal
      />
    );
  };

  const updateCookie = (): void => {
    setCookie(REDIRECT_CONFIRMATION_QR_TEMPLATE_COOKIE_NAME, isChecked ? '1' : '0', 365);
  };

  const onCancelClicked = (): void => {
    dispatch(qrCodeFlow());
  };

  const onConfirmClicked = (): void => {
    updateCookie();
    window.open(getEditPosterURL(webLink.qrTemplateHashedId), '_blank');
    props.onClose();
  };

  const getContent = (): ReactElement<any> => {
    return (
      <PanelContent>
        <div className={'spacing-p-t-1 spacing-p-b-4 spacing-p-l-4 spacing-p-r-4'}>
          <Text val={window.i18next.t('pmwjs_open_qr_template_in_new_tab_confirmation_info')} className={'flexbox -text-align-center'} size={TextSize.XSMALL} />
          <div className={'flex-row-align-center spacing-p-t-4'}>
            <Checkbox
              size={IconSize.SIZE_ICON_12}
              className={'spacing-m-1'}
              isChecked={isChecked}
              onClick={(): void => {
                setIsChecked(!isChecked);
              }}
            />
            <Text val={window.i18next.t('pmwjs_dont_show_this_again')} className={'spacing-p-l-1'} size={TextSize.XXSMALL} />
          </div>
          <div className={'flex-row-align-center spacing-p-t-4'}>
            <Button text={window.i18next.t('pmwjs_cancel')} customClasses={'spacing-m-r-4'} isFullWidth type={Type.SECONDARY} size={Size.SMALL} onClick={onCancelClicked} />
            <Button text={window.i18next.t('pmwjs_open_in_new_tab')} isFullWidth type={Type.PRIMARY} size={Size.SMALL} onClick={onConfirmClicked} />
          </div>
        </div>
      </PanelContent>
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}
